/**
 * One stocktake session (#3031): counting (works on a phone), submit, the
 * discrepancy report and approval. Rendered inside the inventory tab.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { BarcodeScannerDialog } from '@/components/inventory/BarcodeScannerDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { AddFoundPartDialog } from './AddFoundPartDialog';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { formatCentsForDisplay } from '@/utils/money';
import {
  MAX_COUNTED_QUANTITY,
  MAX_COUNTS_PER_REQUEST,
  STATUS_COLOR,
  STOCKTAKE_REASON_CODES,
  apiErrorCode,
  apiErrorDetails,
  bookQuantity,
  canApprove,
  canEditCounts,
  linesMissingReason,
  parseCountedQuantity,
  reasonMissing,
  useApproveStocktake,
  useRecordCounts,
  useStocktakeAction,
  useStocktakeReport,
  useStocktakeSession,
  type CountEntry,
  type StocktakeLine,
  type StocktakeLineFilter,
  type StocktakeReasonCode,
  type StocktakeReport,
  type StocktakeReportLine,
} from './stocktakeSessions';

interface Draft {
  quantity: string;
  reasonCode: StocktakeReasonCode | null;
  note: string;
  /** The line version the counter saw when they started typing. */
  baseVersion: number;
  /**
   * Unit and book quantity of the line when the draft started. A save is built
   * from the draft alone, so it does not depend on the line being on screen
   * (a filter or a search can hide a line that still has unsaved text).
   */
  unit: string;
  book: number;
  /** Bumped by every edit; a save clears the draft only if it is still the revision that was sent. */
  rev: number;
  /** Set when someone else counted the line meanwhile. */
  conflict?: { countedQuantity: number | null; countedBy: string | null };
}

/** A draft that is ready to send, with the revision it was read at. */
interface PreparedCount {
  entry: CountEntry;
  rev: number;
}

/** True when the draft cannot be saved yet: bad quantity, or a difference without its reason. */
function draftIncomplete(draft: Draft): boolean {
  const parsed = parseCountedQuantity(draft.quantity);
  if (!parsed.ok) return true;
  return reasonMissing(parsed.value - draft.book, draft.reasonCode, draft.note);
}

function prepareCount(lineId: string, draft: Draft): PreparedCount | null {
  const parsed = parseCountedQuantity(draft.quantity);
  if (!parsed.ok || draftIncomplete(draft)) return null;
  return {
    rev: draft.rev,
    entry: {
      lineId,
      countedQuantity: parsed.value,
      unit: draft.unit,
      expectedVersion: draft.baseVersion,
      // Sent as entered even when the local book says the count matches: the
      // server compares with live stock, keeps the reason only for a real
      // difference, and the draft's book can be stale.
      reasonCode: draft.reasonCode,
      note: draft.note.trim() === '' ? null : draft.note.trim(),
    },
  };
}

const SIGNED = (value: number): string => (value > 0 ? `+${value}` : String(value));

function CountCard({
  line,
  draft,
  editable,
  people,
  onChange,
  onSave,
  onTakeOver,
  saving,
  reasonRequired = false,
}: {
  line: StocktakeLine;
  draft: Draft | undefined;
  editable: boolean;
  /** The server said this line's difference still needs a reason. */
  reasonRequired?: boolean;
  people: Record<string, string>;
  onChange: (patch: Partial<Draft>) => void;
  onSave: () => void;
  onTakeOver: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const shown = draft?.quantity ?? (line.countedQuantity === null ? '' : String(line.countedQuantity));
  const parsed = shown === '' ? null : parseCountedQuantity(shown);
  const invalid = parsed !== null && !parsed.ok;
  const book = bookQuantity(line);
  const difference = parsed?.ok ? parsed.value - book : null;
  const reasonCode = draft ? draft.reasonCode : line.reasonCode;
  const note = draft ? draft.note : (line.note ?? '');
  const needsReason = (difference !== null && difference !== 0) || reasonRequired;
  // The same rule as `reasonMissing`, also for a line the server flagged.
  const reasonIsMissing = needsReason && (reasonCode === null || (reasonCode === 'other' && note.trim() === ''));
  const incomplete = parsed === null || invalid || reasonIsMissing;
  const location = line.binLocationCode ?? t('stocktakeSessions.noBin');
  const counted = line.countedQuantity !== null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`stocktake-line-${line.id}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {line.partName}
        </Typography>
        <Chip size="small" variant="outlined" label={line.sku} sx={{ fontFamily: 'monospace' }} />
        <Chip size="small" variant="outlined" label={location} />
        {counted && !draft && (
          <Chip
            size="small"
            color={line.countedQuantity === line.onHandAtCount ? 'success' : 'warning'}
            label={
              line.countedQuantity === line.onHandAtCount
                ? t('stocktakeSessions.matches')
                : t('stocktakeSessions.differs', { diff: SIGNED((line.countedQuantity ?? 0) - (line.onHandAtCount ?? 0)) })
            }
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t('stocktakeSessions.book', { qty: book, unit: line.unit })}
        {line.countedBy && people[line.countedBy] ? ` · ${t('stocktakeSessions.countedBy', { name: people[line.countedBy] })}` : ''}
      </Typography>

      {draft?.conflict && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={onTakeOver}>
              {t('stocktakeSessions.keepMine')}
            </Button>
          }
        >
          {t('stocktakeSessions.conflict', {
            qty: draft.conflict.countedQuantity ?? '–',
            name: (draft.conflict.countedBy && people[draft.conflict.countedBy]) || t('stocktakeSessions.someoneElse'),
          })}
        </Alert>
      )}

      {reasonRequired && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t('stocktakeSessions.reasonRequiredByServer')}
        </Alert>
      )}

      {editable && (
        <Box sx={{ display: 'grid', gap: 1.5, mt: 1.5 }}>
          <TextField
            label={t('stocktakeSessions.countedQuantity')}
            value={shown}
            onChange={(e) => onChange({ quantity: e.target.value })}
            error={invalid}
            helperText={invalid ? t('stocktakeSessions.quantityInvalid', { max: MAX_COUNTED_QUANTITY }) : undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft && !incomplete) onSave();
            }}
            slotProps={{
              htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 7, style: { fontSize: '1.25rem' } },
              input: { endAdornment: <InputAdornment position="end">{line.unit}</InputAdornment> },
            }}
            fullWidth
          />
          {needsReason && (
            <>
              <FormControl
                fullWidth
                size="small"
                error={reasonIsMissing && reasonCode === null}
              >
                <InputLabel id={`reason-${line.id}`}>{t('stocktakeSessions.reason')}</InputLabel>
                <Select
                  labelId={`reason-${line.id}`}
                  label={t('stocktakeSessions.reason')}
                  value={reasonCode ?? ''}
                  onChange={(e) => {
                    const value: string = e.target.value;
                    onChange({ reasonCode: value === '' ? null : (value as StocktakeReasonCode) });
                  }}
                >
                  {STOCKTAKE_REASON_CODES.map((code) => (
                    <MenuItem key={code} value={code}>
                      {t(`stocktakeSessions.reasons.${code}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label={reasonCode === 'other' ? t('stocktakeSessions.noteRequired') : t('stocktakeSessions.note')}
                value={note}
                onChange={(e) => onChange({ note: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                multiline
                fullWidth
              />
            </>
          )}
          {draft && (
            <Button variant="contained" onClick={onSave} disabled={incomplete || saving}>
              {tc('save')}
            </Button>
          )}
        </Box>
      )}
    </Paper>
  );
}

function ReportTable({ report, showResolution }: { report: StocktakeReport; showResolution: boolean }) {
  const { t } = useTranslation('inventory');
  const rows = report.lines.filter((line) => line.state !== 'match');
  const stateChip = (line: StocktakeReportLine) => (
    <Chip
      size="small"
      color={line.state === 'blocked' ? 'error' : line.state === 'review' ? 'warning' : line.state === 'discrepancy' ? 'warning' : 'default'}
      label={t(`stocktakeSessions.lineState.${line.state}`)}
    />
  );
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip label={t('stocktakeSessions.summary.counted', { count: report.summary.counted })} color="success" size="small" />
        <Chip label={t('stocktakeSessions.summary.uncounted', { count: report.summary.uncounted })} size="small" />
        <Chip label={t('stocktakeSessions.summary.discrepancies', { count: report.summary.discrepancyLines })} color="warning" size="small" />
        {report.summary.reviewRequired > 0 && (
          <Chip label={t('stocktakeSessions.summary.review', { count: report.summary.reviewRequired })} color="warning" size="small" />
        )}
        {report.summary.blocked > 0 && (
          <Chip label={t('stocktakeSessions.summary.blocked', { count: report.summary.blocked })} color="error" size="small" />
        )}
        {showResolution && (
          <>
            <Chip label={t('stocktakeSessions.summary.corrected', { count: report.summary.applied })} color="success" size="small" />
            {report.summary.rejected > 0 && (
              <Chip label={t('stocktakeSessions.summary.rejected', { count: report.summary.rejected })} size="small" />
            )}
            {report.summary.skipped > 0 && (
              <Chip label={t('stocktakeSessions.summary.skipped', { count: report.summary.skipped })} size="small" />
            )}
          </>
        )}
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('stocktakeSessions.summary.values', {
          surplus: formatCentsForDisplay(report.summary.surplusValueCents),
          shortage: formatCentsForDisplay(report.summary.shortageValueCents),
          net: formatCentsForDisplay(report.summary.netValueCents),
        })}
      </Typography>
      {showResolution && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, mt: -1 }}>
          {t('stocktakeSessions.storedOutcome')}
          {report.summary.unappliedValueCents !== 0
            ? ` · ${t('stocktakeSessions.summary.unapplied', { value: formatCentsForDisplay(report.summary.unappliedValueCents) })}`
            : ''}
        </Typography>
      )}
      {rows.length === 0 ? (
        <Alert severity="success">{t('stocktakeSessions.reportAllMatch')}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('stocktakeSessions.reportTitle')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('stocktakeSessions.col.part')}</TableCell>
                <TableCell>{t('stocktakeSessions.col.location')}</TableCell>
                <TableCell align="right">{t('stocktakeSessions.col.book')}</TableCell>
                <TableCell align="right">{t('stocktakeSessions.col.counted')}</TableCell>
                <TableCell align="right">{t('stocktakeSessions.col.difference')}</TableCell>
                <TableCell align="right">{t('stocktakeSessions.col.value')}</TableCell>
                <TableCell>{t('stocktakeSessions.col.reason')}</TableCell>
                <TableCell>{showResolution ? t('stocktakeSessions.col.result') : t('stocktakeSessions.col.state')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((line) => (
                <TableRow key={line.lineId}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {line.partName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {line.sku}
                    </Typography>
                  </TableCell>
                  <TableCell>{line.binLocationCode ?? t('stocktakeSessions.noBin')}</TableCell>
                  <TableCell align="right">{line.onHandAtCount ?? line.snapshotOnHand}</TableCell>
                  <TableCell align="right">{line.countedQuantity ?? '–'}</TableCell>
                  <TableCell align="right">{line.discrepancy === null ? '–' : SIGNED(line.discrepancy)}</TableCell>
                  <TableCell align="right">
                    {line.discrepancyValueCents === null ? '–' : formatCentsForDisplay(line.discrepancyValueCents)}
                  </TableCell>
                  <TableCell>{line.reasonCode ? t(`stocktakeSessions.reasons.${line.reasonCode}`) : '–'}</TableCell>
                  <TableCell>
                    {showResolution ? t(`stocktakeSessions.resolution.${line.resolution}`) : stateChip(line)}
                    {line.basis === 'live_reference' && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {t('stocktakeSessions.liveReference')}
                      </Typography>
                    )}
                    {line.state === 'review' && line.movedSinceCount !== null && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {t('stocktakeSessions.movedSince', { diff: SIGNED(line.movedSinceCount) })}
                      </Typography>
                    )}
                    {line.state === 'blocked' && line.blockedCode && (
                      <Typography variant="caption" display="block" color="error">
                        {t(`stocktakeSessions.blocked.${line.blockedCode}`)}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

type Decision = 'acknowledge' | 'reject' | '';

function ApproveDialog({
  open,
  onClose,
  report,
  sessionId,
}: {
  open: boolean;
  onClose: () => void;
  report: StocktakeReport | undefined;
  sessionId: string;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const approve = useApproveStocktake(sessionId);
  const [note, setNote] = useState('');
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  const review = report?.lines.filter((line) => line.state === 'review') ?? [];
  const blocked = report?.lines.filter((line) => line.state === 'blocked') ?? [];
  const undecided = review.some((line) => !decisions[line.lineId]);

  const submit = () => {
    approve.mutate(
      {
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        acknowledgedLineIds: review.filter((l) => decisions[l.lineId] === 'acknowledge').map((l) => l.lineId),
        // A blocked line can only be left as it is.
        rejectedLineIds: [
          ...blocked.map((l) => l.lineId),
          ...review.filter((l) => decisions[l.lineId] === 'reject').map((l) => l.lineId),
        ],
      },
      {
        onSuccess: (result) => {
          notify.success(
            t('stocktakeSessions.approved'),
            t('stocktakeSessions.approvedDesc', { applied: result.applied, rejected: result.rejected }),
          );
          onClose();
        },
        onError: (error) => {
          const code = apiErrorCode(error);
          const message =
            code === 'STOCKTAKE_SELF_APPROVAL'
              ? t('stocktakeSessions.errors.selfApproval')
              : code === 'STOCKTAKE_APPROVAL_BLOCKED'
                ? t('stocktakeSessions.errors.approvalChanged')
                : t('stocktakeSessions.errors.approveFailed');
          notify.error(tc('error'), message);
        },
      },
    );
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth mobileTitle={t('stocktakeSessions.approveTitle')}>
      <DialogTitle>{t('stocktakeSessions.approveTitle')}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('stocktakeSessions.approveInfo')}
        </Alert>
        {report && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('stocktakeSessions.summary.values', {
              surplus: formatCentsForDisplay(report.summary.surplusValueCents),
              shortage: formatCentsForDisplay(report.summary.shortageValueCents),
              net: formatCentsForDisplay(report.summary.netValueCents),
            })}
          </Typography>
        )}
        {review.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              {t('stocktakeSessions.reviewInfo')}
            </Alert>
            {review.map((line) => (
              <Paper key={line.lineId} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {line.partName} · {line.binLocationCode ?? t('stocktakeSessions.noBin')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {t('stocktakeSessions.reviewLine', {
                    book: line.onHandAtCount ?? 0,
                    counted: line.countedQuantity ?? 0,
                    moved: SIGNED(line.movedSinceCount ?? 0),
                    current: line.currentOnHand,
                  })}
                </Typography>
                <FormControl size="small" fullWidth>
                  <InputLabel id={`decision-${line.lineId}`}>{t('stocktakeSessions.decision')}</InputLabel>
                  <Select
                    labelId={`decision-${line.lineId}`}
                    label={t('stocktakeSessions.decision')}
                    value={decisions[line.lineId] ?? ''}
                    onChange={(e) => setDecisions((prev) => ({ ...prev, [line.lineId]: e.target.value as Decision }))}
                  >
                    <MenuItem value="acknowledge">{t('stocktakeSessions.decisions.acknowledge')}</MenuItem>
                    <MenuItem value="reject">{t('stocktakeSessions.decisions.reject')}</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            ))}
          </Box>
        )}
        {blocked.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('stocktakeSessions.blockedInfo', { count: blocked.length })}
          </Alert>
        )}
        <TextField
          label={t('stocktakeSessions.approvalNote')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 1000 } }}
          multiline
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={!report || undecided || approve.isPending}>
          {t('stocktakeSessions.approve')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}

export default function StocktakeSessionView({ sessionId, onBack }: { sessionId: string; onBack: () => void }) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const canAdjust = useAuthStore((s) => s.hasPermission('inventory.adjust'));
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const [filter, setFilter] = useState<StocktakeLineFilter>('pending');
  const [search, setSearch] = useState('');
  const query = useDebounce(search.trim(), 300);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [scannerOpen, setScannerOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [allowUncounted, setAllowUncounted] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [addFoundOpen, setAddFoundOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Lines the server says still need a reason (from a save or a refused submit). */
  const [reasonRequiredIds, setReasonRequiredIds] = useState<ReadonlySet<string>>(new Set());

  const flagReasonRequired = (ids: readonly string[]) => {
    if (ids.length === 0) return;
    setReasonRequiredIds((prev) => new Set([...prev, ...ids]));
    // Saved lines leave the "pending" tab; show where they are.
    setFilter('discrepancy');
    notify.warning(t('stocktakeSessions.reasonRequiredTitle'), t('stocktakeSessions.reasonRequiredDesc', { count: ids.length }));
  };

  const detailQuery = useStocktakeSession(sessionId, filter, query);
  const detail = detailQuery.data;
  const session = detail?.session;
  const showReport = session?.status === 'submitted' || session?.status === 'approved';
  const reportQuery = useStocktakeReport(sessionId, showReport);
  const record = useRecordCounts(sessionId);
  const start = useStocktakeAction('start');
  const submit = useStocktakeAction('submit');
  const cancel = useStocktakeAction('cancel');

  const lines = useMemo(() => detail?.lines.data ?? [], [detail]);
  const lineById = useMemo(() => new Map(lines.map((line) => [line.id, line])), [lines]);
  const editable = session !== undefined && canAdjust && canEditCounts(session);
  const dirtyIds = Object.keys(drafts);
  const hiddenDrafts = dirtyIds.filter((id) => !lineById.has(id)).length;

  const patchDraft = (line: StocktakeLine, patch: Partial<Draft>) =>
    setDrafts((prev) => {
      const current = prev[line.id] ?? {
        quantity: line.countedQuantity === null ? '' : String(line.countedQuantity),
        reasonCode: line.reasonCode,
        note: line.note ?? '',
        baseVersion: line.version,
        unit: line.unit,
        book: bookQuantity(line),
        rev: 0,
      };
      return { ...prev, [line.id]: { ...current, ...patch, rev: current.rev + 1 } };
    });

  /** Drops the drafts a save wrote, but keeps text typed while it was in flight (rebased on the new version). */
  const settleSaved = (batch: PreparedCount[], written: StocktakeLine[]) => {
    const byId = new Map(written.map((line) => [line.id, line]));
    setDrafts((prev) => {
      const next = { ...prev };
      for (const { entry, rev } of batch) {
        const draft = next[entry.lineId];
        if (!draft) continue;
        if (draft.rev === rev) {
          delete next[entry.lineId];
          continue;
        }
        const line = byId.get(entry.lineId);
        const { conflict: _conflict, ...rest } = draft;
        next[entry.lineId] = {
          ...rest,
          baseVersion: line?.version ?? entry.expectedVersion + 1,
          book: line?.onHandAtCount ?? draft.book,
        };
      }
      return next;
    });
  };

  const markConflicts = (error: unknown) => {
    const details = apiErrorDetails<{
      conflicts: { lineId: string; countedQuantity: number | null; countedBy: string | null }[];
    }>(error);
    setDrafts((prev) => {
      const next = { ...prev };
      for (const conflict of details?.conflicts ?? []) {
        const draft = next[conflict.lineId];
        if (draft) {
          next[conflict.lineId] = {
            ...draft,
            conflict: { countedQuantity: conflict.countedQuantity, countedBy: conflict.countedBy },
          };
        }
      }
      return next;
    });
  };

  /**
   * Saves drafts from the drafts themselves, hidden lines included, in requests
   * of at most MAX_COUNTS_PER_REQUEST. Each request is awaited before the next,
   * and clears exactly the revisions it sent.
   */
  const saveLines = async (ids: string[]) => {
    if (saving) return;
    const prepared: PreparedCount[] = [];
    let incomplete = 0;
    for (const id of ids) {
      const draft = drafts[id];
      if (!draft) continue;
      const count = prepareCount(id, draft);
      if (count) prepared.push(count);
      else incomplete += 1;
    }
    if (incomplete > 0) notify.error(tc('error'), t('stocktakeSessions.errors.incompleteSkipped', { count: incomplete }));
    if (prepared.length === 0) return;
    setSaving(true);
    let saved = 0;
    try {
      for (let start = 0; start < prepared.length; start += MAX_COUNTS_PER_REQUEST) {
        const batch = prepared.slice(start, start + MAX_COUNTS_PER_REQUEST);
        const result = await record.mutateAsync(batch.map((item) => item.entry));
        settleSaved(batch, result.data);
        saved += batch.length;
        const written = new Set(result.data.map((line) => line.id));
        const stillMissing = new Set(linesMissingReason(result.data));
        // A line saved with its reason no longer needs the warning.
        setReasonRequiredIds((prev) => new Set([...prev].filter((id) => !written.has(id) || stillMissing.has(id))));
        flagReasonRequired([...stillMissing]);
      }
    } catch (error) {
      if (apiErrorCode(error) === 'STOCKTAKE_LINE_CONFLICT') {
        markConflicts(error);
        void detailQuery.refetch();
        notify.error(tc('error'), t('stocktakeSessions.errors.conflict'));
      } else {
        notify.error(tc('error'), t('stocktakeSessions.errors.countFailed'));
      }
    } finally {
      setSaving(false);
    }
    if (saved > 0) notify.success(t('stocktakeSessions.countsSaved'), t('stocktakeSessions.countsSavedDesc', { count: saved }));
  };

  const transition = (action: 'start' | 'submit' | 'cancel', body?: Record<string, unknown>) => {
    const mutation = action === 'start' ? start : action === 'submit' ? submit : cancel;
    mutation.mutate(
      { id: sessionId, ...(body ? { body } : {}) },
      {
        onSuccess: () => {
          setSubmitOpen(false);
          setCancelOpen(false);
          notify.success(t(`stocktakeSessions.done.${action}`));
        },
        onError: (error) => {
          const code = apiErrorCode(error);
          if (code === 'STOCKTAKE_REASON_REQUIRED') {
            const lineIds = apiErrorDetails<{ lineIds?: string[] }>(error)?.lineIds ?? [];
            if (lineIds.length > 0) {
              setSubmitOpen(false);
              flagReasonRequired(lineIds);
              return;
            }
          }
          const known = [
            'STOCKTAKE_REASON_REQUIRED',
            'STOCKTAKE_UNCOUNTED_LINES',
            'STOCKTAKE_NOTHING_COUNTED',
            'STOCKTAKE_INVALID_STATE',
            'STOCKTAKE_SCOPE_TOO_LARGE',
          ];
          notify.error(
            tc('error'),
            code && known.includes(code) ? t(`stocktakeSessions.errors.${code}`) : t('stocktakeSessions.errors.actionFailed'),
          );
        },
      },
    );
  };

  if (detailQuery.isLoading) return <LinearProgress aria-label={tc('loading')} />;
  if (detailQuery.isError || !detail || !session) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          {t('stocktakeSessions.back')}
        </Button>
        <Alert severity="error">{t('stocktakeSessions.errors.loadFailed')}</Alert>
      </Box>
    );
  }

  const { progress, people } = detail;
  const percent = progress.total === 0 ? 0 : (progress.counted / progress.total) * 100;
  const mayApprove = canApprove(session, userId, canAdjust, detail.counters ?? []);
  const open = ['planned', 'counting', 'submitted'].includes(session.status);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
        {t('stocktakeSessions.back')}
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="h6">{session.name}</Typography>
        <Chip size="small" variant="outlined" label={t(`stocktakeSessions.kinds.${session.kind}`)} />
        <Chip size="small" color={STATUS_COLOR[session.status]} label={t(`stocktakeSessions.status.${session.status}`)} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {session.plannedFor ? t('stocktakeSessions.plannedForShort', { date: session.plannedFor }) : ''}
        {session.approvedBy && people[session.approvedBy]
          ? ` ${t('stocktakeSessions.approvedBy', { name: people[session.approvedBy] })}`
          : ''}
        {session.cancelReason ? ` ${t('stocktakeSessions.cancelledBecause', { reason: session.cancelReason })}` : ''}
      </Typography>

      {canAdjust && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {session.status === 'planned' && (
            <Button variant="contained" onClick={() => transition('start')} disabled={start.isPending}>
              {t('stocktakeSessions.startCounting')}
            </Button>
          )}
          {session.status === 'counting' && (
            <Button
              variant="contained"
              onClick={() => {
                setAllowUncounted(false);
                setSubmitOpen(true);
              }}
              disabled={progress.counted === 0 || dirtyIds.length > 0}
            >
              {t('stocktakeSessions.submit')}
            </Button>
          )}
          {mayApprove && (
            <Button variant="contained" color="success" onClick={() => setApproveOpen(true)}>
              {t('stocktakeSessions.approve')}
            </Button>
          )}
          {open && (
            <Button color="error" onClick={() => setCancelOpen(true)}>
              {t('stocktakeSessions.cancel')}
            </Button>
          )}
        </Box>
      )}
      {session.status === 'submitted' && canAdjust && !mayApprove && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('stocktakeSessions.waitingForApprover')}
        </Alert>
      )}

      {(session.status === 'counting' || session.status === 'planned') && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {t('stocktakeSessions.progress')}
            </Typography>
            <Typography variant="body2">
              {progress.counted} / {progress.total}
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 1 }} aria-label={t('stocktakeSessions.progress')} />
          {progress.discrepancies > 0 && (
            <Chip size="small" color="warning" sx={{ mt: 1.5 }} label={t('stocktakeSessions.summary.discrepancies', { count: progress.discrepancies })} />
          )}
        </Paper>
      )}

      {session.status === 'planned' && <Alert severity="info">{t('stocktakeSessions.plannedInfo')}</Alert>}

      {session.status === 'counting' && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('stocktakeSessions.searchPart')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                htmlInput: { 'aria-label': t('stocktakeSessions.searchPart') },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button variant="outlined" startIcon={<QrCodeScannerIcon />} onClick={() => setScannerOpen(true)}>
              {t('stocktakeSessions.scan')}
            </Button>
            {editable && (
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddFoundOpen(true)}>
                {t('stocktakeSessions.addFound')}
              </Button>
            )}
          </Box>
          <Tabs value={filter} onChange={(_, value: StocktakeLineFilter) => setFilter(value)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Tab value="pending" label={t('stocktakeSessions.filters.pending')} />
            <Tab value="counted" label={t('stocktakeSessions.filters.counted')} />
            <Tab value="discrepancy" label={t('stocktakeSessions.filters.discrepancy')} />
            <Tab value="all" label={t('stocktakeSessions.filters.all')} />
          </Tabs>
          {!canAdjust && <Alert severity="info" sx={{ mb: 2 }}>{t('stocktakeSessions.readOnly')}</Alert>}
          {hiddenDrafts > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('stocktakeSessions.draftsHidden', { count: hiddenDrafts })}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, pb: dirtyIds.length > 0 ? 10 : 0 }}>
            {lines.map((line) => (
              <CountCard
                key={line.id}
                line={line}
                draft={drafts[line.id]}
                editable={editable}
                people={people}
                saving={saving}
                reasonRequired={reasonRequiredIds.has(line.id)}
                onChange={(patch) => patchDraft(line, patch)}
                onSave={() => void saveLines([line.id])}
                onTakeOver={() =>
                  setDrafts((prev) => {
                    const draft = prev[line.id];
                    if (!draft) return prev;
                    const { conflict: _conflict, ...rest } = draft;
                    return { ...prev, [line.id]: { ...rest, baseVersion: line.version, book: bookQuantity(line) } };
                  })
                }
              />
            ))}
          </Box>
          {lines.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('stocktakeSessions.noLines')}</Typography>
            </Paper>
          )}
          {detailQuery.hasNextPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="outlined" onClick={() => void detailQuery.fetchNextPage()} disabled={detailQuery.isFetchingNextPage}>
                {t('stocktakeSessions.loadMore', { shown: lines.length, total: detail.lines.total })}
              </Button>
            </Box>
          )}
          {dirtyIds.length > 0 && (
            <Paper
              elevation={4}
              sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, p: 2, zIndex: 1200, display: 'flex', justifyContent: 'center' }}
            >
              <Button variant="contained" size="large" onClick={() => void saveLines(dirtyIds)} disabled={saving}>
                {t('stocktakeSessions.saveAll', { count: dirtyIds.length })}
              </Button>
            </Paper>
          )}
        </>
      )}

      {showReport && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {t('stocktakeSessions.reportTitle')}
          </Typography>
          {reportQuery.isLoading && <LinearProgress aria-label={tc('loading')} />}
          {reportQuery.isError && <Alert severity="error">{t('stocktakeSessions.errors.loadFailed')}</Alert>}
          {reportQuery.data && <ReportTable report={reportQuery.data} showResolution={session.status === 'approved'} />}
        </Box>
      )}

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(barcode) => {
          setFilter('all');
          setSearch(barcode);
          setScannerOpen(false);
        }}
        title={t('stocktakeSessions.scanPart')}
      />

      <AddFoundPartDialog
        open={addFoundOpen}
        onClose={() => setAddFoundOpen(false)}
        session={session}
        onAdded={(_line, part) => {
          // Show the new, uncounted line: search by its part number across every filter.
          setFilter('all');
          setSearch(part.sku);
        }}
      />

      <ApproveDialog open={approveOpen} onClose={() => setApproveOpen(false)} report={reportQuery.data} sessionId={sessionId} />

      <ResponsiveDialog open={submitOpen} onClose={() => setSubmitOpen(false)} maxWidth="xs" fullWidth mobileTitle={t('stocktakeSessions.submitTitle')}>
        <DialogTitle>{t('stocktakeSessions.submitTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>{t('stocktakeSessions.submitMessage')}</Typography>
          {progress.counted < progress.total && (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('stocktakeSessions.uncountedWarning', { count: progress.total - progress.counted })}
              </Alert>
              <FormControlLabel
                control={<Checkbox checked={allowUncounted} onChange={(e) => setAllowUncounted(e.target.checked)} />}
                label={t('stocktakeSessions.allowUncounted')}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button color="inherit" onClick={() => setSubmitOpen(false)}>
            {tc('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => transition('submit', { allowUncounted })}
            disabled={submit.isPending || (progress.counted < progress.total && !allowUncounted)}
          >
            {t('stocktakeSessions.submit')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth mobileTitle={t('stocktakeSessions.cancelTitle')}>
        <DialogTitle>{t('stocktakeSessions.cancelTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('stocktakeSessions.cancelMessage')}</Typography>
          <TextField
            autoFocus
            fullWidth
            label={t('stocktakeSessions.cancelReason')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button color="inherit" onClick={() => setCancelOpen(false)}>
            {tc('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => transition('cancel', { reason: cancelReason.trim() })}
            disabled={cancelReason.trim() === '' || cancel.isPending}
          >
            {t('stocktakeSessions.cancel')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
}
